import React from 'react';
import DOMPurify from 'dompurify';
import {
  Stack,
  HStack,
  Box,
  Tooltip,
  Text,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  IconButton,
  type BoxProps,
} from '@chakra-ui/react';
import { transparentize } from '@chakra-ui/theme-tools';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamationCircle, faNoteSticky, faEllipsisH } from '@fortawesome/pro-regular-svg-icons';
import { parseDate, formatDate } from '../../utilities/temporal-utils';

import { IMessage } from 'types';

interface IMessageBubble {
  messageStyle?: 'outgoing' | 'incoming' | 'system' | 'note' | 'note-outgoing';
  authorStyle?: 'uni' | 'enroly' | 'student' | 'agent' | 'partner' | 'system';
  headerSize?: 'lg' | 'md' | 'sm' | 'xs';
  hasAvatar?: boolean;
  actions?: IActions[];
}

interface IActions {
  type: 'action' | 'divider';
  onClick?: () => void;
  icon?: IconProp;
  label?: string;
}

const Message = ({
  message,
  author,
  createdAt,
  isMessageSaving,
  isMessageError,
  messageStyle,
  authorStyle,
  headerSize,
  hasAvatar,
  actions,
}: IMessage & IMessageBubble): React.ReactElement => {
  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
      node.setAttribute('style', 'color: var(--ds-link-color); text-decoration: underline;');
    }
  });

  const dateObj =
    typeof createdAt === 'string' ? parseDate(createdAt) : parseDate(new Date(createdAt).toISOString().split('T')[0]);
  const createdDate = dateObj ? formatDate(dateObj, 'DD MMM YYYY') : 'Invalid date';
  const createdTime = formatDate(createdAt, 'HH:mm');

  const getMessageStyle = (): BoxProps => {
    switch (messageStyle) {
      case 'outgoing':
        return {
          flexDirection: 'row-reverse',
          justifyContent: 'end',
          alignItems: 'end',
          bgColor: 'blue.500',
          borderRadius: '20px 20px 0 20px',
          color: 'white',
          px: 4,
          py: 2,
          ml: 16,
          mr: 4,
          _dark: {
            bgColor: transparentize(`blue.500`, 0.5),
          },
        };

      case 'note':
        return {
          bgColor: 'yellow.50',
          borderRadius: 'md',
          px: 4,
          py: 2,
          ml: 4,
          mr: 16,
          _dark: {
            bgColor: transparentize(`yellow.300`, 0.16),
          },
        };

      case 'note-outgoing':
        return {
          flexDirection: 'row-reverse',
          justifyContent: 'end',
          alignItems: 'end',
          bgColor: 'yellow.50',
          borderRadius: 'md',
          px: 4,
          py: 2,
          ml: 16,
          mr: 4,
          _dark: {
            bgColor: transparentize(`yellow.300`, 0.16),
          },
        };

      case 'system':
        return {
          color: 'muted',
          borderLeftWidth: 4,
          px: 3,
          py: 0,
          ml: 4,
          mr: 8,
        };

      case 'incoming':
      default:
        return {
          flexDirection: 'row',
          justifyContent: 'start',
          alignItems: 'start',
          bgColor: 'chakra-subtle-bg',
          borderRadius: '20px 20px 20px 0',
          px: 4,
          py: 2,
          ml: 4,
          mr: 16,
        };
    }
  };

  const getAuthorStyle = (): BoxProps => {
    switch (authorStyle) {
      case 'uni':
        return { color: 'purple.500', _dark: { color: 'purple.300' } };

      case 'enroly':
        return { color: 'violet.500', _dark: { color: 'violet.300' } };

      case 'student':
        return { color: 'pink.500', _dark: { color: 'pink.300' } };

      case 'agent':
        return { color: 'orange.700', _dark: { color: 'orange.300' } };

      case 'partner':
        return { color: 'teal.600', _dark: { color: 'teal.300' } };

      default:
        return { color: 'gray.600', _dark: { color: 'gray.300' } };
    }
  };

  const renderActions = (): React.ReactElement => {
    return (
      <MenuList>
        {actions?.map((action, index) => {
          if (action.type === 'divider') {
            return <MenuDivider key={index} />;
          }
          return (
            <MenuItem key={index} gap={2} onClick={action.onClick}>
              {action.icon && <FontAwesomeIcon icon={action.icon} fixedWidth className="utility-icon" />} {action.label}
            </MenuItem>
          );
        })}
      </MenuList>
    );
  };

  let sanitizedMessage = <Box>{message}</Box>;
  if (messageStyle === 'system') {
    sanitizedMessage = (
      <Box
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(message),
        }}
      />
    );
  }

  return (
    <HStack
      alignItems={'start'}
      flexDirection={getMessageStyle()?.flexDirection}
      justifyContent={getMessageStyle().justifyContent}
      mt={6}
      mb={4}
      ml={getMessageStyle().ml}
      mr={getMessageStyle().mr}
      pl={1}
      pr={1}
      maxWidth="100%"
      position={'relative'}
      _hover={{
        '.actions-button': {
          opacity: 1,
        },
      }}
    >
      {hasAvatar ? <Avatar mt={6} name={author} size={'base'} /> : null}

      {actions ? (
        <Menu>
          <Tooltip hasArrow placement="top" label="Actions">
            <MenuButton
              as={IconButton}
              aria-label="Actions"
              className="actions-button"
              icon={<FontAwesomeIcon icon={faEllipsisH} />}
              size={'xs'}
              variant={'outline'}
              colorScheme={'gray'}
              bgColor={'chakra-body-bg'}
              position={'absolute'}
              top={4}
              left={messageStyle === 'outgoing' || messageStyle === 'note-outgoing' ? 'initial' : -2}
              right={messageStyle === 'outgoing' || messageStyle === 'note-outgoing' ? -2 : 'initial'}
              opacity={0}
              _focus={{
                boxShadow: 'outline',
                opacity: 1,
              }}
              _dark={{
                color: 'whiteAlpha.700',
                _hover: {
                  bgColor: 'gray.800',
                  color: 'whiteAlpha.800',
                },
                _active: {
                  bgColor: 'gray.700',
                  color: 'whiteAlpha.900',
                },
              }}
              sx={{
                '&[data-active]': {
                  opacity: 1,
                },
              }}
            />
          </Tooltip>

          {renderActions()}
        </Menu>
      ) : null}

      <Stack alignItems={getMessageStyle().alignItems} maxWidth="100%" spacing={0.5}>
        <Box fontSize={headerSize || 'xs'}>
          <Box as="span" fontWeight="semibold" mr={2} {...getAuthorStyle()}>
            {author}
          </Box>

          <Box as="span" color="muted">
            <Box as="b" fontWeight="medium">
              {createdDate}
            </Box>

            {` at ${createdTime}`}
          </Box>

          {isMessageError ? (
            <Tooltip
              hasArrow
              placement="top-end"
              label="We were unable to send the message. Please refresh or try again later."
            >
              <Box as="span" color="error" px={1} tabIndex={0}>
                <FontAwesomeIcon icon={faExclamationCircle} size="lg" />
              </Box>
            </Tooltip>
          ) : null}
        </Box>

        <Box
          width="fit-content"
          maxWidth="400px"
          bgColor={getMessageStyle().bgColor}
          borderLeftWidth={getMessageStyle()?.borderLeftWidth}
          borderRadius={getMessageStyle().borderRadius}
          color={getMessageStyle().color}
          px={getMessageStyle().px}
          py={getMessageStyle().py}
          opacity={isMessageSaving ? 0.4 : 1}
          transitionProperty="opacity"
          transitionDuration="0.1s"
          _dark={{
            bgColor: getMessageStyle()._dark?.bgColor,
          }}
        >
          {messageStyle === 'note' || messageStyle === 'note-outgoing' ? (
            <Box
              fontSize={'2xs'}
              lineHeight={4}
              minWidth={'160px'}
              color={'yellow.700'}
              _dark={{ color: 'yellow.400' }}
            >
              <FontAwesomeIcon icon={faNoteSticky} fixedWidth size="lg" />

              <Text as="span" variant={'allcaps'} mb={0} ml={1}>
                Internal Note
              </Text>
            </Box>
          ) : null}
          {sanitizedMessage}
        </Box>
      </Stack>
    </HStack>
  );
};

export { Message };
